/**
 * I promemoria attivi, visti dal divano.
 *
 * ## Perché non è la schermata del telefono
 *
 * Perché era quella, e si leggeva col naso sullo schermo. `PhoneRemindersScreen` dimensiona il
 * titolo con `min(7vw, 28px)`: su un telefono è il 7% di 390 pixel, cioè 27; su 1920 il tetto scatta
 * e restano **28 pixel** da guardare a tre metri. Le due schermate non differiscono per un ritocco
 * di stile — una si tocca e l'altra si percorre col telecomando — quindi non c'era un parametro da
 * aggiungere, c'era una schermata da scrivere.
 *
 * Le misure sono quelle delle altre schermate del televisore: titolo 64, riga 32, quando 25,
 * respiro 72. Vedi `FavoritesScreen`, con cui condivide anche la grammatica dei tasti.
 *
 * ## Un elenco in colonna, non una griglia
 *
 * I preferiti sono caselle affiancate perché un canale si riconosce dal marchio. Qui ogni riga è una
 * frase — titolo, giorno, ora, canale — e due frasi affiancate su uno schermo da tre metri si
 * leggono una per volta comunque: la colonna è l'unica forma in cui su e giù vogliono dire quel che
 * sembra.
 *
 * ## OK guarda, la stella toglie
 *
 * Gli stessi due bersagli della riga della guida e della casella dei preferiti: destra porta sulla
 * stella, sinistra torna. Un elenco di promemoria in cui OK cancella sarebbe l'unico posto dell'app
 * dove il tasto che conferma distrugge.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { eventWhen, type ReminderEntry } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { eventLine } from './HomeScreen.js';
import { scrollFocusedIntoView } from '../scrollFocusedIntoView.js';

export interface RemindersScreenProps {
  readonly focusPort: FocusPort;
  readonly entries: ReadonlyArray<ReminderEntry>;
  readonly channelNameOf: (channelId: string) => string;
  /** L'istante del server: decide «stasera» e «domani», e non va dedotto qui. */
  readonly nowMs: number;
  readonly onWatch: (channelId: string) => void;
  readonly onRemove: (channelId: string, startMs: number) => void;
  readonly onExit: () => void;
}

export function RemindersScreen({
  focusPort,
  entries,
  channelNameOf,
  nowMs,
  onWatch,
  onRemove,
  onExit,
}: RemindersScreenProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState(0);
  /** Se il fuoco è sulla stella invece che sulla riga. Due bersagli, come nei preferiti. */
  const [onStar, setOnStar] = useState(false);
  const frameRef = useRef<HTMLDivElement | null>(null);

  const handlers = useRef({ onWatch, onRemove, onExit });
  useEffect(() => {
    handlers.current = { onWatch, onRemove, onExit };
  }, [onWatch, onRemove, onExit]);

  /*
    Il cursore si riporta dentro i limiti quando l'elenco si accorcia.

    Togliendo l'ultimo promemoria il cursore punterebbe a una riga che non c'è, e premere OK non
    farebbe niente: il modo peggiore di rompersi, perché sembra un telecomando scarico. E l'elenco si
    accorcia **da sé**, quando un programma finisce.
  */
  useEffect(() => {
    setCursor((at) => (entries.length === 0 ? 0 : Math.min(at, entries.length - 1)));
  }, [entries.length]);

  const move = useCallback(
    (delta: number) => {
      setCursor((current) => {
        const next = current + delta;
        // Ai bordi si **resta**, non si avvolge: come nei preferiti e nella guida.
        if (next < 0 || next >= entries.length) return current;
        return next;
      });
    },
    [entries.length],
  );

  useEffect(() => {
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        handlers.current.onExit();
        return true;
      }
      if (key.kind === 'select') {
        const voce = entries[cursor];
        if (!voce) return true;
        if (onStar) handlers.current.onRemove(voce.channelId, voce.startMs);
        else handlers.current.onWatch(voce.channelId);
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'right') setOnStar(true);
      if (key.direction === 'left') setOnStar(false);
      if (key.direction === 'up') move(-1);
      if (key.direction === 'down') move(1);
      return true;
    });
  }, [focusPort, entries, cursor, move, onStar]);

  /*
    La riga sotto il cursore si porta in vista.

    L'elenco scorre dentro la colonna, e senza questo il fuoco scende sotto il bordo dello schermo:
    si preme giù e **non succede niente di visibile**. È la stessa funzione che usano la guida e la
    vetrina.
  */
  useEffect(() => {
    scrollFocusedIntoView(
      frameRef.current?.querySelector<HTMLElement>('[data-reminder-row][data-focused="1"]'),
    );
  }, [cursor, onStar, entries.length]);

  return (
    <div
      ref={frameRef}
      data-reminders="1"
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '72px 72px 80px',
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
        boxSizing: 'border-box',
      }}
    >
      <h1 style={{ font: `800 64px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>
        {translator.t('menu.reminders')}
      </h1>

      {entries.length === 0 ? (
        /*
          Vuoto, e si dice **dove si mettono**: è l'unica cosa utile che questa pagina può dire
          quando non ha niente da mostrare. Stessa scelta dei preferiti.
        */
        <p
          data-reminders-empty="1"
          style={{ font: `500 25px/1.4 ${FONT_STACK}`, color: colors.textDim, margin: 0 }}
        >
          {translator.t('reminders.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {entries.map((voce, index) => {
            const focused = index === cursor;
            const channel = channelNameOf(voce.channelId);
            /*
              Il quando lo compone `eventLine`, la stessa dell'hero: «Stasera alle 20:45» è una frase
              intera in ognuna delle sette lingue. Il canale si ripete **solo** se la prima riga
              porta il titolo — altrimenti sarebbe scritto due volte di fila.
            */
            const quando = eventLine(
              translator,
              eventWhen(voce.startMs, nowMs, false),
              voce.program === null ? null : channel,
            );
            return (
              <li
                key={voce.key}
                data-reminder-row="1"
                data-focused={focused ? '1' : '0'}
                style={{ display: 'flex', alignItems: 'stretch', gap: 16 }}
              >
                <button
                  type="button"
                  data-f="1"
                  data-reminder={voce.key}
                  data-focused={focused && !onStar ? '1' : '0'}
                  onClick={() => handlers.current.onWatch(voce.channelId)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    padding: '20px 28px',
                    borderRadius: 16,
                    border: `1px solid ${colors.borderFaint}`,
                    background: colors.panel,
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    outline: focused && !onStar ? `4px solid ${colors.accent}` : 'none',
                    outlineOffset: 4,
                  }}
                >
                  <span
                    style={{
                      font: `700 32px/1.2 ${FONT_STACK}`,
                      letterSpacing: '-.01em',
                      // Un titolo lungo non deve allargare la riga: la colonna ha una larghezza, e
                      // `minmax(auto,…)` qui è la stessa trappola della griglia della vetrina.
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {voce.program === null ? channel : voce.program.title}
                  </span>
                  <span style={{ font: `500 25px ${FONT_STACK}`, color: colors.textDim }}>
                    {quando}
                  </span>
                </button>

                <button
                  type="button"
                  data-f="1"
                  data-remove={voce.key}
                  data-focused={focused && onStar ? '1' : '0'}
                  aria-pressed
                  /*
                    Il nome dell'azione sta in `aria-label`: la stella da sola non dice che questo
                    tasto **toglie**, e qui l'errore non si annulla.
                  */
                  aria-label={translator.t('guide.action.reminderSet')}
                  onClick={() => handlers.current.onRemove(voce.channelId, voce.startMs)}
                  style={{
                    flex: 'none',
                    width: 96,
                    borderRadius: 16,
                    border: `1px solid ${colors.border}`,
                    background: 'transparent',
                    color: colors.accent,
                    font: `800 36px ${FONT_STACK}`,
                    lineHeight: 1,
                    cursor: 'pointer',
                    outline: focused && onStar ? `4px solid ${colors.accent}` : 'none',
                    outlineOffset: 3,
                  }}
                >
                  ★
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
